import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  FAREWELL_RETRY_TOKEN_PARAM,
  FAREWELL_SEVV_USER_PARAM,
  FAREWELL_SIGNUP_CLOSED_PARAM,
  isRetryableGuestFarewell,
} from '../../utils/guestFarewellUrl'
import { DEFAULT_SIGNUP_WAITLIST_URL, signupWaitlistUrl } from '../../utils/signupWaitlist'
import { AuthMessages } from '../AuthenticationDialog.messages'
import { ServiceFactory } from '../../core/factories/ServiceFactory'

/**
 * Standalone farewell page for guests, rendered at /meeting/farewell.
 *
 * Reads `reason`, `company` and `sevvUser` from URL search params so it
 * works after a full page navigation (no Redux state required).
 * Shows why the session ended together with the Sevv CTAs on a single screen;
 * guests who already use Sevv in another company get a link back instead of the sign-up pitch.
 * While Registration Mode is Closed (`signupClosed`, SC-443) the pitch offers the waitlist
 * instead of a free trial.
 */
export function getFarewellMessage(reason: string): string {
  switch (reason) {
    case 'removed':
      return 'You have been removed from the meeting.'
    case 'abandoned':
      return 'The meeting host has left.'
    case 'already_connected':
      return 'You are already connected from another device. Please close that session first.'
    case 'join_failed':
      return "We couldn't connect you to the meeting."
    case 'office_not_open':
      return "The meeting office isn't open yet."
    case 'ended':
    default:
      return 'The meeting has ended.'
  }
}

export default function GuestFarewellScreen() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const reason = params.get('reason') || 'ended'
  const companyName = params.get('company') || undefined
  const isSevvUser = params.get(FAREWELL_SEVV_USER_PARAM) === '1'
  const retryToken = params.get(FAREWELL_RETRY_TOKEN_PARAM)
  const signupClosed = params.get(FAREWELL_SIGNUP_CLOSED_PARAM) === '1'
  const showsWaitlist = signupClosed && !isSevvUser && !isRetryableGuestFarewell(reason)

  // The page renders outside AppController, so the admin's waitlist address is read here. The
  // button shows at once with the default and takes the configured address when it arrives.
  const [waitlistUrl, setWaitlistUrl] = useState(DEFAULT_SIGNUP_WAITLIST_URL)
  useEffect(() => {
    if (!showsWaitlist) return
    let cancelled = false
    ServiceFactory.getInstance()
      .getWorkspaceService()
      .getSettings()
      .then((settings) => {
        if (!cancelled) setWaitlistUrl(signupWaitlistUrl(settings))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [showsWaitlist])

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.85)',
        color: '#fff',
        p: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 460 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          {getFarewellMessage(reason)}
        </Typography>
        {isRetryableGuestFarewell(reason) ? (
          <>
            <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
              {reason === 'office_not_open'
                ? 'Try again once your host has joined.'
                : 'Please check your connection and try again.'}
            </Typography>
            {retryToken && (
              <Button
                variant="contained"
                href={`/meeting/join?token=${encodeURIComponent(retryToken)}`}
                sx={{
                  bgcolor: 'var(--sevv-primary, #6366f1)',
                  '&:hover': { bgcolor: 'var(--sevv-primary-dark, #4f46e5)' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Try again
              </Button>
            )}
          </>
        ) : (
          <>
            <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
              {companyName
                ? `Thanks for visiting ${companyName}!`
                : 'Thanks for visiting!'}
            </Typography>
            {isSevvUser ? (
              <Button
                variant="contained"
                href="/"
                sx={{
                  bgcolor: 'var(--sevv-primary, #6366f1)',
                  '&:hover': { bgcolor: 'var(--sevv-primary-dark, #4f46e5)' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Open Sevv
              </Button>
            ) : (
              <>
                <Typography variant="body1" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                  This meeting was powered by Sevv — a virtual office where teams work together in real time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)' }}>
                  {signupClosed
                    ? AuthMessages.SIGNUP_CLOSED_NOTICE
                    : 'Meet face-to-face, collaborate in rooms, and stay connected — all from your browser.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {signupClosed ? (
                    <Button
                      variant="contained"
                      href={waitlistUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${AuthMessages.SIGNUP_WAITLIST_CTA} ${AuthMessages.OPENS_IN_NEW_TAB}`}
                      endIcon={<OpenInNewIcon aria-hidden />}
                      sx={{
                        bgcolor: 'var(--sevv-primary, #6366f1)',
                        '&:hover': { bgcolor: 'var(--sevv-primary-dark, #4f46e5)' },
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      {AuthMessages.SIGNUP_WAITLIST_CTA}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      href="/"
                      target="_blank"
                      sx={{
                        bgcolor: '#6366f1',
                        '&:hover': { bgcolor: '#4f46e5' },
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Try Sevv free
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    href="https://sevv.com"
                    target="_blank"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: '#fff',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.1)' },
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Request a walkthrough
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
